import type { LearnerType, PracticeModule, PracticeActivity, LearningStyleType } from '@/types'

// ========================
// 学习者类型档案（5种）
// ========================
export interface LearnerTypeProfile {
  type: LearnerType
  /** 短标签，如"侦探" */
  tag: string
  name: string
  avatar: string
  description: string
  emoji: string
  /** 主题色 key（对应 dashboard colorMap） */
  color: 'teal' | 'coral' | 'gold' | 'berry' | 'purple'
  /** 一句话学习建议 */
  tip: string
}

export const learnerTypeProfiles: LearnerTypeProfile[] = [
  {
    type: 'story',
    tag: '侦探',
    name: '侦探',
    avatar: '宝藏侦探',
    description: '喜欢听故事、探险和解谜，通过剧情学习新知识。',
    emoji: '🕵️',
    color: 'purple',
    tip: '把知识点放进故事里，记得最牢',
  },
  {
    type: 'challenge',
    tag: '勇士',
    name: '勇士',
    avatar: '先锋勇士',
    description: '喜欢通过闯关、比赛、排行榜激励自己学习。',
    emoji: '⚔️',
    color: 'coral',
    tip: '限时闯关与破纪录最能激发你的斗志',
  },
  {
    type: 'game',
    tag: '学徒',
    name: '学徒',
    avatar: '魔法学徒',
    description: '喜欢通过游戏、配对、道具来学习，边玩边学。',
    emoji: '🪄',
    color: 'teal',
    tip: '配对消除、连连看让你不知不觉学会',
  },
  {
    type: 'task',
    tag: '建筑大师',
    name: '建筑大师',
    avatar: '建筑大师',
    description: '喜欢有明确目标和步骤，通过达成目标获得成就感。',
    emoji: '🔨',
    color: 'gold',
    tip: '按步骤完成任务，一步一个脚印',
  },
  {
    type: 'social',
    tag: '吟游诗人',
    name: '吟游诗人',
    avatar: '吟游诗人',
    description: '喜欢与同伴一起学习，通过交流、合作和分享获得动力。',
    emoji: '🤝',
    color: 'berry',
    tip: '角色扮演与对话让你学得最自然',
  },
]

export const learnerTypeMap: Record<LearnerType, LearnerTypeProfile> = Object.fromEntries(
  learnerTypeProfiles.map((p) => [p.type, p]),
) as Record<LearnerType, LearnerTypeProfile>

// ========================
// 五大练习模块信息（新增词汇）
// ========================
export interface ModuleInfo {
  id: PracticeModule
  name: string
  short: string
  emoji: string
  color: 'teal' | 'coral' | 'gold' | 'berry' | 'purple'
  desc: string
}

export const moduleInfos: ModuleInfo[] = [
  { id: 'listen', name: '听力练习', short: '听', emoji: '👂', color: 'teal', desc: '听音辨义、听故事、听指令' },
  { id: 'speak', name: '口语练习', short: '说', emoji: '🗣️', color: 'coral', desc: '配音、跟读、角色对话' },
  { id: 'read', name: '阅读练习', short: '读', emoji: '📖', color: 'gold', desc: '绘本、限时阅读、图文匹配' },
  { id: 'write', name: '书写练习', short: '写', emoji: '✏️', color: 'berry', desc: '填空、拼写Boss、拼单词' },
  { id: 'vocab', name: '词汇练习', short: '词', emoji: '📝', color: 'purple', desc: '单词记忆、词义配对、拼词闯关' },
]

export const moduleMap: Record<PracticeModule, ModuleInfo> = Object.fromEntries(
  moduleInfos.map((m) => [m.id, m]),
) as Record<PracticeModule, ModuleInfo>

// ========================
// 5 类型 × 5 模块 活动矩阵（新增词汇模块）
// ========================
// 每个单元格是一个具体活动，mode 用于调度交互组件
export const activityMatrix: Record<LearnerType, Record<PracticeModule, PracticeActivity>> = {
  story: {
    listen: { module: 'listen', type: 'story', title: '消失的钟声', desc: '进入迷雾小镇 · CASE 01：找回钟楼里消失的声音', emoji: '🎧', mode: 'detective-adventure' },
    speak: { module: 'speak', type: 'story', title: '沉默的目击者', desc: '进入迷雾小镇 · CASE 02：让沉默的目击者重新开口', emoji: '🎙️', mode: 'detective-adventure' },
    read: { module: 'read', type: 'story', title: '被撕碎的密信', desc: '进入迷雾小镇 · CASE 03：复原被撕碎的秘密纸条', emoji: '🔎', mode: 'detective-adventure' },
    write: { module: 'write', type: 'story', title: '空白的案件簿', desc: '进入迷雾小镇 · CASE 04：补回侦探手册里的空白', emoji: '✏️', mode: 'detective-adventure' },
    vocab: { module: 'vocab', type: 'story', title: '词汇线索墙', desc: '收集案件相关的单词线索，拼出完整词汇图', emoji: '📝', mode: 'detective-adventure' },
  },
  challenge: {
    listen: { module: 'listen', type: 'challenge', title: '听音限时闯关', desc: '限时听音选义，连击破纪录', emoji: '⏱️', mode: 'listen-challenge' },
    speak: { module: 'speak', type: 'challenge', title: '回音峡谷', desc: '4 关口语冒险，朗读当前单元句子并闯关', emoji: '🔊', mode: 'speak-challenge' },
    read: { module: 'read', type: 'challenge', title: '限时阅读', desc: '限时读完 passages，速答理解题', emoji: '⚡', mode: 'read-challenge' },
    write: { module: 'write', type: 'challenge', title: '拼写Boss战', desc: '拼对单词攻击Boss，击败它', emoji: '⚔️', mode: 'write-challenge' },
    vocab: { module: 'vocab', type: 'challenge', title: '词汇闪电战', desc: '限时词义速配，看谁拼得最多', emoji: '⚡', mode: 'write-challenge' },
  },
  game: {
    listen: { module: 'listen', type: 'game', title: '听音配对消除', desc: '翻牌听音，配对消除单词', emoji: '🀄', mode: 'listen-game' },
    speak: { module: 'speak', type: 'game', title: '语音角色对话', desc: '选择并说出角色的回应', emoji: '🗣️', mode: 'speak-game' },
    read: { module: 'read', type: 'game', title: '图文连连看', desc: '把英文句子和图意连起来', emoji: '🔗', mode: 'read-game' },
    write: { module: 'write', type: 'game', title: '拖拽拼词大冒险', desc: '拖拽彩色字母到轮廓中，拼出正确单词', emoji: '🧩', mode: 'write-game' },
    vocab: { module: 'vocab', type: 'game', title: '词义消消乐', desc: '匹配单词与中文释义，消除得分', emoji: '🎯', mode: 'listen-game' },
  },
  task: {
    listen: { module: 'listen', type: 'task', title: '听指令完成任务', desc: '听指令，选出对应的图片', emoji: '📋', mode: 'listen-task' },
    speak: { module: 'speak', type: 'task', title: '看图描述输出', desc: '看图用目标词描述，说到即过关', emoji: '🖼️', mode: 'speak-task' },
    read: { module: 'read', type: 'task', title: '按步骤读说明', desc: '按步骤阅读说明，逐项完成', emoji: '📝', mode: 'read-task' },
    write: { module: 'write', type: 'task', title: '填表式拼写', desc: '边听边把单词填进表格', emoji: '📊', mode: 'write-task' },
    vocab: { module: 'vocab', type: 'task', title: '词汇任务卡', desc: '按任务卡完成词汇学习打卡', emoji: '📋', mode: 'listen-task' },
  },
  social: {
    listen: { module: 'listen', type: 'social', title: '同伴对话听力', desc: '听同伴对话，回答谁说了什么', emoji: '👂', mode: 'listen-social' },
    speak: { module: 'speak', type: 'social', title: '角色扮演对话', desc: '与搭档轮流角色扮演对话', emoji: '🎪', mode: 'speak-social' },
    read: { module: 'read', type: 'social', title: '共读式阅读', desc: '和阅读伙伴轮流共读段落', emoji: '📖', mode: 'read-social' },
    write: { module: 'write', type: 'social', title: '合作完成句子', desc: '合作补全句子，拼出完整表达', emoji: '🤝', mode: 'write-social' },
    vocab: { module: 'vocab', type: 'social', title: '词汇接龙战', desc: '两人轮流说出同主题单词对决', emoji: '🤝', mode: 'listen-social' },
  },
}

/** 获取某类型某模块的活动配置 */
export function getActivity(type: LearnerType, module: PracticeModule): PracticeActivity {
  return activityMatrix[type][module]
}

/** 学习路径五类内容 → 练习模块的映射（用于深链，新增词汇） */
export const contentToModule: Record<string, PracticeModule> = {
  '单词游戏': 'listen',
  '口语': 'speak',
  '拼写': 'write',
  '绘本阅读': 'read',
  '词汇': 'vocab',
}

/** 把 LearningStyleType（可能为 null）安全转为 LearnerType，默认 game */
export function resolveLearnerType(t: LearningStyleType | LearnerType | null | undefined): LearnerType {
  switch (t) {
    case 'story':
    case 'challenge':
    case 'game':
    case 'task':
    case 'social':
      return t
    case 'imagination':
      return 'story'
    case 'visual':
      return 'game'
    case 'expression':
      return 'social'
    default:
      return 'game'
  }
}
